import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { Button, IconButton, type Tone } from './Button'
import { suppressTooltipsBriefly } from './Tooltip'
import { LinearProgress } from './Progress'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  icon?: string | undefined
  /** Una frase in body-medium on-surface-variant, primo elemento del corpo: serve a spiegare, non a dare istruzioni sui campi. */
  supportingText?: ReactNode | undefined
  size?: 'sm' | 'md' | 'lg' | undefined
  /** Pulsanti in fondo (di solito <Button variant="text"> e <Button variant="filled">). */
  actions?: ReactNode | undefined
  /** 'form' avvolge intestazione, corpo e azioni in un <form>: i pulsanti submit in `actions` funzionano. */
  as?: 'div' | 'form' | undefined
  onSubmit?: ((e: FormEvent<HTMLFormElement>) => void) | undefined
  /**
   * Sotto i 600px il dialog occupa lo schermo (header 56 con X, barra delle azioni 56 in fondo) [S dialogs].
   * Default: true se `as="form"` (ci sono dei campi), false altrimenti.
   */
  fullscreenOnCompact?: boolean | undefined
  /** Se false Esc e click sullo sfondo non chiudono (es. salvataggio in corso). */
  dismissible?: boolean | undefined
  /** Click sullo sfondo chiude il dialog. Spento di default: nei moduli un click fuori farebbe perdere i dati inseriti. */
  closeOnScrim?: boolean | undefined
  className?: string | undefined
  children?: ReactNode
}

const EXIT_MS = 200

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/**
 * Dialog su <dialog> nativo + showModal(): focus trap, Esc e ritorno del focus all'elemento
 * che lo aveva prima dell'apertura sono forniti dal browser. ::backdrop = scrim/32%.
 */
export function Dialog({
  open,
  onClose,
  title,
  icon,
  supportingText,
  size = 'md',
  actions,
  as = 'div',
  onSubmit,
  fullscreenOnCompact: fullscreenProp,
  dismissible = true,
  closeOnScrim = false,
  className,
  children,
}: DialogProps) {
  const fullscreenOnCompact = fullscreenProp ?? (as === 'form')
  const ref = useRef<HTMLDialogElement | null>(null)
  const titleId = useId()
  const [present, setPresent] = useState(open)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    if (open) {
      setPresent(true)
      setClosing(false)
      return undefined
    }
    if (!present) return undefined
    setClosing(true)
    const t = window.setTimeout(
      () => {
        setPresent(false)
        setClosing(false)
      },
      prefersReducedMotion() ? 0 : EXIT_MS,
    )
    return () => window.clearTimeout(t)
    // `present` e' volutamente fuori dalle dipendenze: reagiamo solo al cambio di `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (present && !el.open) {
      suppressTooltipsBriefly()
      el.showModal()
      // Focus iniziale esplicito: il primo controllo attivo, mai il contenitore che scorre.
      // `data-autofocus` indica il controllo preferito (es. la conferma di useConfirm).
      const first = el.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)') ?? el.querySelector<HTMLElement>('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled):not(.md-dialog__close), [href]')
      first?.focus()
    } else if (!present && el.open) {
      suppressTooltipsBriefly()
      el.close()
    }
  }, [present])

  const body = (
    <>
      <div className={cx('md-dialog__header', icon && 'md-dialog__header--icon')}>
        {fullscreenOnCompact ? (
          <IconButton icon="close" aria-label="Chiudi" className="md-dialog__close" disabled={!dismissible} onClick={onClose} />
        ) : null}
        {icon ? <Icon name={icon} size={24} className="md-dialog__icon" /> : null}
        <h2 id={titleId} className="md-dialog__title">
          {title}
        </h2>
      </div>
      <div className="md-dialog__body">
        {supportingText ? <p className="md-dialog__supporting">{supportingText}</p> : null}
        {children}
      </div>
      {actions ? <div className="md-dialog__actions">{actions}</div> : null}
    </>
  )

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      data-closing={closing || undefined}
      className={cx('md-dialog', `md-dialog--${size}`, fullscreenOnCompact && 'md-dialog--fullscreen-compact', className)}
      onCancel={(e) => {
        e.preventDefault()
        if (dismissible) onClose()
      }}
      onClick={(e) => {
        // Il click sullo scrim ha come target il <dialog> stesso (il pannello occupa tutto l'elemento).
        if (dismissible && closeOnScrim && e.target === e.currentTarget) onClose()
      }}
    >
      {present ? (
        as === 'form' ? (
          <form className="md-dialog__panel" onSubmit={onSubmit}>
            {body}
          </form>
        ) : (
          <div className="md-dialog__panel">{body}</div>
        )
      ) : null}
    </dialog>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel: string
  cancelLabel?: string | undefined
  tone?: Tone | undefined
  icon?: string | undefined
  onConfirm: () => void
  onCancel: () => void
}

/** Sostituisce `window.confirm()`. Azioni `text` [S dialog.action]. Il focus iniziale va su "Annulla" (azione sicura). */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Annulla',
  tone = 'primary',
  icon,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      icon={icon}
      size="sm"
      actions={
        <>
          <Button variant="text" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="text" tone={tone} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="md-dialog__message">{message}</div>
    </Dialog>
  )
}

export interface DialogStepsProps {
  /** Passo corrente, da 1 a `total`. */
  current: number
  total: number
  /** Nome del passo ("Anteprima"). */
  label: string
}

/**
 * Indicatore di un dialog a piu' passi: "Passo 2 di 3: Anteprima" e la barra di avanzamento. Primo elemento del
 * corpo. A ogni cambio di passo (non alla prima comparsa) riceve il focus, cosi' chi usa la tastiera o uno screen
 * reader sente dove si trova; tabIndex -1: si raggiunge solo da programma.
 */
export function DialogSteps({ current, total, label }: DialogStepsProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    ref.current?.focus()
  }, [current])
  const text = `Passo ${current} di ${total}: ${label}`
  return (
    <div ref={ref} tabIndex={-1} className="md-dialog__steps">
      <span className="md-dialog__steps-label">{text}</span>
      <LinearProgress value={(current / total) * 100} aria-label={text} />
    </div>
  )
}
